import type { BillingConfig } from "./store";

/** Public configuration needs no session or SDK. Starting this from the small
 * entry module overlaps the request with React and pricing downloads. */
export const BILLING_CONFIG_PREFETCH_TTL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;
type ConfigRequest = { expiresAt: number; promise: Promise<BillingConfig | null> };
let pending: ConfigRequest | null = null;

export function requestBillingConfig(): Promise<BillingConfig | null> {
  if (pending && performance.now() < pending.expiresAt) return pending.promise;
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!url || !key) return Promise.resolve(null);
  const request: ConfigRequest = {
    expiresAt: performance.now() + BILLING_CONFIG_PREFETCH_TTL_MS,
    promise: Promise.resolve(null),
  };
  request.promise = (async () => {
    try {
      const response = await fetch(`${url.replace(/\/$/, "")}/functions/v1/billing-config`, {
        method: "POST", cache: "no-store", credentials: "omit",
        headers: { apikey: key, Authorization: `Bearer ${key}` },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (!response.ok) return null;
      const data: unknown = await response.json();
      if (!data || typeof data !== "object" ||
          typeof (data as BillingConfig).enabled !== "boolean" ||
          !["sandbox", "production"].includes((data as BillingConfig).environment)) return null;
      return data as BillingConfig;
    } catch {
      return null;
    }
  })();
  pending = request;
  // Failed speculative work is safe to ignore; a later normal boot can retry.
  void request.promise.then((data) => { if (!data && pending === request) pending = null; });
  return request.promise;
}

/** The response remains in this document's memory only. It is never an
 * offline configuration or an entitlement cache. */
export function prefetchBillingConfig(): void {
  void requestBillingConfig();
}
